// Playing in a world: input, the world view, effects and sounds, the HUD and the windows.
import { app, type Screen } from '../app';
import { api, content } from '../api';
import { settings, saveSettings } from '../settings';
import { Widget, widget, fontSize } from '../ui/compose';
import { WorldRenderer, type CharacterVisual } from './renderer';
import { Effects } from './effects';
import { Input } from './input';
import { TouchControls } from './touch';
import { Hud } from './hud';
import { Chat } from './chat';
import { Windows } from './windows';
import { Backdrop } from './backdrop';
import type { BagInfo, Frame, GameEvent, TileEntityInfo } from './types';
import { Facing } from '../../../shared/src/constants';

const UP = 1, DOWN = 2, LEFT = 4, RIGHT = 8, RUN = 16;
/** How far (tiles) a fully pushed right stick aims: the farthest a cursor-targeted spell lands. */
const TOUCH_REACH = 6;

export class GameScreen implements Screen {
  private renderer!: WorldRenderer;
  private effects!: Effects;
  private input!: Input;
  private touch!: TouchControls;
  private hud!: Hud;
  private chat!: Chat;
  windows!: Windows;
  frameState: Frame | null = null;
  private zoneSerial = -1;
  private objectsRev = -1;
  private zoom = 3;
  private paused = false;
  private pauseMenu: Widget | null = null;
  private playerList!: Widget;
  private playerListRev = '';
  private mapSaveTimer = 0;
  private lastFeet = { x: 0, y: 0 };
  private stepDistance = 0;
  private lastChopBeat = -1;
  private harvestTool = 'none';
  private mobMoving = new Map<number, boolean>();
  private zonesEntered = 0;
  private leaving = false;

  enter(): void {
    Backdrop.hide();
    this.renderer = new WorldRenderer(app.sprites, { chunk: (cx, cy) => { const b = api.chunk(cx, cy); return b.length ? b : null; } });
    for (const l of this.renderer.layers) app.scene.addChild(l);
    this.effects = new Effects(this.renderer, app.sprites);
    this.input = new Input(app.pixi.canvas);
    this.touch = new TouchControls(app.pixi.canvas.parentElement!, app.pixi.canvas, { chat: () => this.chat.openWith(''), menu: () => this.escape() });
    this.hud = new Hud(m => this.windows.menu(m), () => api.releaseCorpse());
    this.hud.showDebug = settings.showDebug;
    this.chat = new Chat();
    this.windows = new Windows(this);
    this.playerList = widget('box', { class: 'panel player-list', style: { display: 'none' } });
    this.hud.bottomLeft.prepend(this.chat.root);
    app.ui.append(this.hud.root, this.playerList, this.hud.deathScreen);
    this.windows.mount(app.ui);
    this.chat.onName = (name, x, y) => this.windows.chatNameMenu(name, x, y);
    this.chat.onIgnoreList = () => this.windows.toggleIgnores();
    this.chat.onFriendList = () => this.windows.toggleFriends();
    this.chat.onSettings = (x, y) => this.windows.chatSettingsMenu(x, y);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('beforeunload', this.onUnload);
  }

  exit(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('beforeunload', this.onUnload);
    this.input.dispose();
    this.touch.dispose();
    this.windows.dispose();
    this.effects.clear();
    this.renderer.destroy();
    settings.showDebug = this.hud.showDebug;
    saveSettings();
  }

  private onUnload = () => api.saveMapMemory();

  /** Opens the chat input (used by windows: "Whisper"). */
  openChat(prefix: string): void { this.chat.openWith(prefix); }

  get session(): Frame | null { return this.frameState; }

  /** Keys that toggle things (movement is read each frame instead). */
  private onKey = (e: KeyboardEvent) => {
    if (this.input.typing()) return;
    if (e.code === 'Escape') { this.escape(); return; }
    if (this.paused) return;
    if (e.code === 'Enter' && !e.altKey) { this.chat.openWith(''); e.preventDefault(); return; }
    if (e.code === 'Slash' || e.code === 'NumpadDivide') { this.chat.openWith('/'); e.preventDefault(); return; }
    if (e.code === 'F3') { this.hud.showDebug = !this.hud.showDebug; e.preventDefault(); return; }
    if (e.code === 'Equal' || e.code === 'NumpadAdd') this.zoomBy(1);
    if (e.code === 'Minus' || e.code === 'NumpadSubtract') this.zoomBy(-1);
    this.windows.key(e);
  };

  /** Esc (or the touch Menu button): closes the top window, else toggles the pause menu. */
  private escape(): void {
    if (!this.windows.escape()) this.setPaused(!this.paused);
  }

  private zoomBy(d: number): void {
    settings.zoomOffset += d;
    saveSettings();
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    this.pauseMenu?.dispose();
    this.pauseMenu = null;
    if (!paused) return;
    const button = (text: string, fn: () => void) => widget('button', { class: 'button', onclick: () => { app.audio.play('sfx_ui_click', 0.5, 0); fn(); } }, text);
    const panel = widget('box', { class: 'panel col', style: { minWidth: `120px`, padding: `6px` } },
      widget('box', { class: 'accent center' }, 'Paused'),
      button('Resume', () => this.setPaused(false)),
      button('Settings', () => this.windows.openSettings()),
      button('Leave World', () => this.leave('You left the world.', false)));
    this.pauseMenu = widget('box', { class: 'screen pause' }, panel);
    app.ui.append(this.pauseMenu);
  }

  leave(message: string, isError: boolean): void {
    if (this.leaving) return;
    this.leaving = true;
    api.saveMapMemory();
    api.leaveWorld(null, false);
    api.logout(null);
    void import('../screens/menus').then(m => app.show(m.afterWorld(message, isError)));
  }

  frame(dt: number): void {
    if (this.leaving) return;
    const input = this.input;
    const typing = input.typing();
    let buttons = 0;
    if (!typing && !this.paused) {
      if (input.down('KeyW') || input.down('ArrowUp')) buttons |= UP;
      if (input.down('KeyS') || input.down('ArrowDown')) buttons |= DOWN;
      if (input.down('KeyA') || input.down('ArrowLeft')) buttons |= LEFT;
      if (input.down('KeyD') || input.down('ArrowRight')) buttons |= RIGHT;
      if (input.shift) buttons |= RUN;
      buttons |= this.touch.moveButtons();
    }
    let ePressed = input.pressed('KeyE') || this.touch.pressed('interact');
    if (ePressed && !typing && this.windows.closeStation()) ePressed = false; // E toggles the station UI
    const blocked = typing || this.paused || this.windows.blocksInteraction() || !document.hasFocus();

    const f = api.frame(dt, buttons, input.down('KeyE') || this.touch.down('interact'), ePressed, blocked) as Frame;
    if (f.state === 'disconnected') { this.leave(f.message || 'Disconnected.', true); return; }
    if (f.state !== 'game') { input.endFrame(); this.touch.endFrame(); return; }
    this.frameState = f;
    const r = this.renderer;

    if (f.zoneSerial !== this.zoneSerial) {
      this.zoneSerial = f.zoneSerial;
      r.clear();
      this.effects.clear();
      this.objectsRev = -1;
    }
    const dirty = api.takeDirtyChunks();
    for (let i = 0; i < dirty.length; i += 2) r.chunkChanged(dirty[i], dirty[i + 1]);
    if (f.rev !== this.objectsRev || dirty.length) {
      this.objectsRev = f.rev;
      const o = api.worldObjects() as { tileEntities: TileEntityInfo[]; bags: BagInfo[] };
      r.setObjects(o.tileEntities, o.bags);
      this.windows.onRevision(f);
    }

    const z = WorldRenderer.computeZoom(app.width, app.height, settings.zoomOffset);
    if (z.offset !== settings.zoomOffset) settings.zoomOffset = z.offset;
    if (input.wheel && !this.windows.capturesWheel()) this.zoomBy(-input.wheel);
    this.zoom = r.zoom = z.zoom;
    r.centerX = f.me.x;
    r.centerY = f.me.y - 8;
    r.dark = f.zone.dark;
    r.indoors = f.zone.indoors;
    r.timeOfDay = f.zone.time;
    r.mapLights = f.zone.lights;
    r.setShots(f.shots);
    r.setHealZones(f.zones);
    r.labelSize = Math.round(fontSize * 0.93);
    r.setResources(f.resources, f.t);
    r.shakeTile = f.hover?.kind === 'progress' ? { x: f.hover.tx!, y: f.hover.ty! } : null;
    r.highlightBag = this.windows.openBag || (f.hover?.kind === 'bag' ? f.hover.id! : 0);

    const chars: CharacterVisual[] = [];
    for (const e of f.ents) {
      if (e.mob) continue;
      chars.push({ key: `p${e.id}`, x: e.x, y: e.y, f: e.f!, moving: e.moving, running: !!e.running, anim: e.anim ?? 0, appearance: e.appearance!,
        armor: e.armor ?? [], flash: e.flash, name: e.name ?? '', nameColor: e.party ? '#8adcf6' : '#ffffff',
        ghost: e.ghost, burning: e.burning, shielded: e.shielded });
    }
    for (const n of f.npcs) {
      chars.push({ key: `n${n.id}`, x: n.x, y: n.y, f: n.f, moving: false, running: false, anim: 0, appearance: n.appearance, armor: n.armor,
        flash: false, name: n.name, nameColor: '#f6e2a0', marker: n.marker, markerColor: n.markerColor });
    }
    const me = f.me;
    chars.push({ key: 'me', x: me.x, y: me.y, f: me.f, moving: me.moving, running: me.running, anim: me.anim, appearance: me.appearance,
      armor: me.armor, flash: me.flash, name: me.name, nameColor: '#ffffff', isLocal: true,
      ghost: me.ghost, burning: me.burning, shielded: me.shielded });
    // Swinging characters step into the swing (Effects.pose); their swords follow the moved body.
    for (const c of chars) {
      const id = c.isLocal ? me.id : c.key.startsWith('p') ? Number(c.key.slice(1)) : -1;
      const pose = id >= 0 ? this.effects.pose(id, f.t) : null;
      if (!pose) continue;
      c.x += pose.dx; c.y += pose.dy;
      if (!c.moving) c.pose = pose.frame;
    }
    // Players in view who spoke lately (Say only reaches those nearby; Party and Map show when the speaker is in view).
    for (const c of chars) if (c.isLocal || c.key.startsWith('p')) c.bubble = this.chat.bubble(c.name, f.t);
    r.update(dt);
    r.ghostVision = f.life.state === 'ghost';
    r.setCorpses(f.corpses);
    r.setCharacters(chars);
    const mobs = f.ents.filter(e => e.mob);
    r.setMobs(mobs);
    r.drawMobTags(mobs);
    this.drawHover(f);
    const positions = new Map<number, { x: number; y: number }>();
    for (const e of f.ents) positions.set(e.id, e);
    positions.set(me.id, me);
    this.effects.setStances([
      ...f.ents.filter(e => !e.mob && (e.blocking || e.stunned)).map(e => ({ entity: e.id, f: e.f ?? 0, shield: e.shield ?? '', stunned: !!e.stunned })),
      ...(me.blocking || me.stunned ? [{ entity: me.id, f: me.f, shield: me.shield, stunned: me.stunned }] : []),
    ]);
    this.effects.draw(f.t, id => positions.get(id) ?? null);
    this.drawHurt(f.hud.hurt);
    r.finishLabels();

    for (const e of f.events) this.handleEvent(e, f);
    this.sounds(f, dt, mobs);

    // Attack: left click on the world or Space, toward the mouse.
    const anyWindow = this.windows.anyOpen() || this.paused || this.chat.open;
    const placing = this.windows.admin.placing !== 'none';
    let clickUsed = input.leftClicked && this.windows.canvasClick();
    if (input.leftClicked && !clickUsed && placing && !anyWindow) {
      const w = r.screenToWorld(input.mouseX, input.mouseY);
      this.windows.admin.placeAt(w.x, w.y);
      clickUsed = true;
    }
    if (!anyWindow && !typing && !placing && ((input.leftClicked && !clickUsed) || input.pressed('Space'))) {
      const w = r.screenToWorld(input.mouseX, input.mouseY);
      api.attack(w.x, w.y);
    }
    // Touch: the right stick auto-fires where it points (the client paces attacks), a tap attacks the way we face.
    const stick = this.touch.aim(), tapped = this.touch.takeTap();
    if (!anyWindow && !typing && !placing && (stick || tapped)) { const w = this.touchAim(f); api.attack(w.x, w.y); }
    if (input.rightClicked && !this.paused) {
      if (placing) this.windows.admin.cancelPlacing();
      // Right-clicking a player opens their menu; anywhere else, holding it blocks.
      else if (!anyWindow && this.windows.worldMenu(r.screenToWorld(input.mouseX, input.mouseY), input.mouseX, input.mouseY)) input.rightHeld = false;
    }
    const touchBlock = this.touch.down('block');
    const aim = touchBlock ? this.touchAim(f) : r.screenToWorld(input.mouseX, input.mouseY);
    api.block((input.rightHeld || touchBlock) && !anyWindow && !typing && !placing, aim.x, aim.y);

    this.hud.update(f, dt, { zoom: this.zoom, hideBanner: this.windows.anyOpen() });
    this.chat.update(f.rev, f.t);
    this.windows.update(f, dt);
    this.updatePlayerList(f, input.down('Tab') && !typing);
    this.mapSaveTimer += dt;
    if (this.mapSaveTimer > 60) { this.mapSaveTimer = 0; api.saveMapMemory(); }
    input.endFrame();
    this.touch.endFrame();
  }

  /** The world point the right stick aims at: up to TOUCH_REACH tiles out by how far it is pushed (spells land
   *  there), or a tile ahead of us when it is idle. */
  private touchAim(f: Frame): { x: number; y: number } {
    const me = f.me, stick = this.touch.aim(), tile = content.tileSize;
    if (stick) {
      const d = tile * (1 + (TOUCH_REACH - 1) * stick.force);
      return { x: me.x + stick.x * d, y: me.y + stick.y * d };
    }
    const [dx, dy] = me.f === Facing.Left ? [-1, 0] : me.f === Facing.Right ? [1, 0] : me.f === Facing.Up ? [0, -1] : [0, 1];
    return { x: me.x + dx * tile, y: me.y + dy * tile };
  }

  screenToWorld(x: number, y: number): { x: number; y: number } { return this.renderer.screenToWorld(x, y); }
  worldToScreen(x: number, y: number): { x: number; y: number } { return this.renderer.worldToScreen(x, y); }
  get mouse(): { x: number; y: number } { return { x: this.input.mouseX, y: this.input.mouseY }; }
  get shiftHeld(): boolean { return this.input.shift; }

  private drawHover(f: Frame): void {
    const hv = f.hover, r = this.renderer;
    if (!hv) return;
    if (hv.kind !== 'progress' && app.ui.hitTest(this.input.mouseX, this.input.mouseY)) return;
    if (hv.kind === 'progress') { r.progressBar(hv.x, hv.y, hv.progress ?? 0); return; }
    if (hv.kind === 'bag' && this.windows.openBag) return;
    if (hv.kind === 'entity' && this.windows.stationOpen()) return;
    r.prompt(hv.label ?? '', hv.x, hv.y, hv.error ? '#f06058' : '#ece2d6', !!hv.below);
  }

  /** A brief red flash at the screen edges when hurt. */
  private drawHurt(left: number): void {
    if (left <= 0) return;
    const g = this.renderer.barsGraphics, w = app.width, hgt = app.height;
    const a = (left / 0.35) * 0.35, e = Math.max(8, Math.floor(hgt / 14));
    g.rect(0, 0, w, e).rect(0, hgt - e, w, e).rect(0, e, e, hgt - 2 * e).rect(w - e, e, e, hgt - 2 * e).fill({ color: 0xc81414, alpha: a });
  }

  private handleEvent(e: GameEvent, f: Frame): void {
    const audio = app.audio, now = f.t;
    switch (e.type) {
      case 'zone':
        if (this.zonesEntered++ > 0) audio.play('sfx_portal', 0.7, 0);
        audio.playMusic(e.dark ? 'music_hollows' : 'music_overworld');
        this.windows.closeAll();
        this.hud.showBanner(e.name, f.hud.server, now);
        break;
      case 'notice':
        this.hud.notice(e.kind, e.text, now);
        if (e.kind === 'success') audio.play('sfx_pickup', 0.6, 0.04);
        else if (e.kind === 'error') audio.play('sfx_error', 0.45, 0, 0, 0, 0.3);
        break;
      case 'swing':
        this.effects.swing(e.entity, e.icon, e.angle, e.arc, now);
        if (e.entity === f.me.id) audio.play('sfx_swing', 0.7, 0.08);
        else { const p = f.ents.find(x => x.id === e.entity); if (p) audio.playAt('sfx_swing', p.x, p.y, 0.6, 0.08); }
        break;
      case 'shot':
      case 'cast': {
        if (e.type === 'shot') this.effects.aim(e.entity, e.icon, e.angle, now);
        if (!e.sound) break;
        if (e.entity === f.me.id) audio.play(e.sound, 0.7, 0.06);
        else { const p = f.ents.find(x => x.id === e.entity); if (p) audio.playAt(e.sound, p.x, p.y, 0.6, 0.06); }
        break;
      }
      case 'impact':
        // Glowing shots (fireballs, missiles) burst in their colour where they stop; arrows just stop (a hit shows its number when the server says so).
        if (e.glow) {
          this.effects.puff(e.x, e.y + 5, /^#[0-9a-f]{6}$/i.test(e.color) ? parseInt(e.color.slice(1), 16) : 0xff8a30, now);
          audio.playAt('sfx_fire_burst', e.x, e.y, 0.7, 0.08);
        }
        break;
      case 'hit':
        if (e.blocked) {
          // Light grey, with what armour and the shield took off: "2 (-2)".
          this.effects.number(e.x, e.y, `${e.damage} (-${e.reduced})`, '#c8c8c8', now);
          if (e.local) audio.play('sfx_block', 0.9, 0.06); else audio.playAt('sfx_block', e.x, e.y, 0.6, 0.06);
        } else if (e.local) {
          this.effects.number(e.x, e.y, String(e.damage), '#ff5a50', now);
          audio.play('sfx_hurt', 0.8, 0.08);
        } else {
          this.effects.number(e.x, e.y, String(e.damage), e.mine ? '#ffe678' : '#ffffff', now);
          audio.playAt('sfx_hit', e.x, e.y, e.mine ? 0.9 : 0.6, 0.1);
        }
        break;
      case 'heal':
        this.effects.number(e.x, e.y, `+${e.amount}`, '#7fe08a', now);
        break;
      case 'block':
        this.effects.number(e.x, e.y - 6, 'Blocked', '#b4c8e6', now); // its hit plays the sound
        break;
      case 'stun':
        this.effects.number(e.x, e.y - 6, 'Stunned!', '#ffe066', now);
        if (e.local) audio.play('sfx_stun', 0.8, 0); else audio.playAt('sfx_stun', e.x, e.y, 0.6, 0);
        break;
      case 'levelup':
        this.effects.puff(e.x, e.y, 0xffd25a, now);
        this.effects.number(e.x, e.y - 6, `Level ${e.level}!`, '#ffd75a', now);
        if (e.local) audio.play('sfx_level_up', 0.9, 0); else audio.playAt('sfx_level_up', e.x, e.y, 0.6, 0);
        break;
      case 'died':
        this.effects.puff(e.x, e.y, e.mob ? 0x55c9b4 : 0xdc3c32, now);
        if (e.local) audio.play('sfx_player_die', 0.9, 0); else audio.playAt(e.mob ? 'sfx_slime_die' : 'sfx_player_die', e.x, e.y, 0.8, 0.08);
        break;
      case 'exp':
        this.effects.number(f.me.x, f.me.y - 10, `+${e.amount} exp`, '#be96ff', now);
        break;
      case 'money':
        this.effects.number(f.me.x, f.me.y - 18, `+${e.text}`, e.color, now);
        audio.play('sfx_pickup', 0.5, 0.1, 0, 0.3);
        break;
      case 'quests':
        // Quest-log changes acknowledge acceptance; rejected requests produce no accepted event.
        if (e.accepted > 0 && this.windows.dialogue.isOpen) this.windows.closeStation();
        if (e.completed > 0) audio.play('sfx_quest_complete', 0.8, 0); else if (e.accepted > 0) audio.play('sfx_quest_accept', 0.7, 0);
        break;
      case 'harvest':
        if (e.phase === 'started') this.harvestTool = e.tool;
        if (e.phase === 'completed') {
          const x = e.x * 16 + 8, y = e.y * 16 + 8;
          if (e.tool === 'axe') audio.playAt('sfx_tree_fall', x, y, 0.9, 0.05);
          else if (e.tool === 'pickaxe') audio.playAt('sfx_rock_break', x, y, 0.9, 0.05);
        }
        break;
      case 'open':
        this.windows.open(e);
        break;
    }
  }

  /** Footsteps, harvest hits and slime hops. */
  private sounds(f: Frame, dt: number, mobs: Frame['ents']): void {
    const audio = app.audio, me = f.me;
    audio.listenerX = me.x;
    audio.listenerY = me.y;
    const moved = Math.hypot(me.x - this.lastFeet.x, me.y - this.lastFeet.y);
    this.lastFeet = { x: me.x, y: me.y };
    if (me.moving && moved < 16) {
      this.stepDistance += moved;
      if (this.stepDistance >= (me.running ? 22 : 17)) {
        this.stepDistance = 0;
        const g = this.groundAt(me.tx, me.ty), n = content.named;
        const step = g === n.groundCobble || g === n.groundStone ? 'sfx_step_stone' : g === n.groundCaveFloor ? 'sfx_step_cave' : 'sfx_step_grass';
        audio.play(step, me.running ? 0.4 : 0.3, 0.12);
      }
    } else if (!me.moving) this.stepDistance = 12;

    if (f.hover?.kind === 'progress' && (this.harvestTool === 'axe' || this.harvestTool === 'pickaxe')) {
      const beat = Math.floor(app.time * 2.5);
      if (beat !== this.lastChopBeat) {
        this.lastChopBeat = beat;
        audio.playAt(this.harvestTool === 'pickaxe' ? 'sfx_mine' : 'sfx_chop', f.hover.tx! * 16 + 8, f.hover.ty! * 16 + 8, 0.8, 0.08);
      }
    }
    const seen = new Map<number, boolean>();
    for (const m of mobs) {
      if (m.moving && !this.mobMoving.get(m.id)) audio.playAt('sfx_slime_hop', m.x, m.y, 0.45, 0.15, 0.05);
      seen.set(m.id, m.moving);
    }
    this.mobMoving = seen;
  }

  private groundAt(tx: number, ty: number): number {
    const b = api.chunk(Math.floor(tx / 32), Math.floor(ty / 32));
    if (!b.length) return 0;
    return b[(((ty % 32) + 32) % 32) * 32 + (((tx % 32) + 32) % 32)];
  }

  /** Tab: the server, how many are online, your latency, and the players on your map (or in your dungeon). */
  private updatePlayerList(f: Frame, show: boolean): void {
    this.playerList.style.display = show ? '' : 'none';
    const key = `${f.rev}:${f.hud.ping}`;
    if (!show || this.playerListRev === key) return;
    this.playerListRev = key;
    const s = api.social() as { server: string; max: number; ping: number; online: number; zone: string; players: { name: string; appearance: number; party: boolean; here: boolean }[] };
    const here = s.players.filter(p => p.here);
    const pingColor = s.ping < 80 ? '#8cdc78' : s.ping < 180 ? '#f0d264' : 'var(--error)';
    this.playerList.replaceChildren(
      widget('box', { class: 'accent center' }, s.server),
      widget('box', { class: 'row' }, widget('text', { class: 'dim grow' }, `${s.online}/${s.max} online`), widget('text', { style: { color: pingColor } }, `${s.ping} ms`)),
      widget('box', { class: 'small dim player-list-zone' }, `${s.zone} (${here.length})`),
      ...here.map(p => widget('box', { class: 'row' }, this.windows.face(p.appearance, 12),
        widget('text', { class: 'grow', style: { color: p.party ? '#78d2f0' : '' } }, p.name))));
  }
}
